import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Avatar,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  InputBase,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Badge,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import BottomNavigation from '../components/BottomNavigation';
import { serviceBookingAPI, providerAPI, homeAPI, searchAPI } from '../services/api';

// Styled components - same as Home screen
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '20ch',
    },
  },
}));

const SearchResultsScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State variables
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [providerTypes, setProviderTypes] = useState(['salon', 'pros']); // Available provider types (controlled by checkboxes)
  const [activeTab, setActiveTab] = useState('salon'); // Currently selected tab for viewing
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [allResults, setAllResults] = useState([]); // Store all results for client-side filtering
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [distanceType, setDistanceType] = useState('distance');
  const [distanceValue, setDistanceValue] = useState(50);
  const [minRating, setMinRating] = useState(0);
  const [userLocation, setUserLocation] = useState(null);
  const [sortBy, setSortBy] = useState('rating-high');
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [error, setError] = useState(null);
  const [providerTypeError, setProviderTypeError] = useState(null);
  
  // Photo gallery auto-scroll state
  const [autoScrollEnabled, setAutoScrollEnabled] = useState({});
  const autoScrollIntervals = useRef({});


  // Enhanced photo gallery auto-scroll functionality
  const startAutoScroll = useCallback((providerId, images) => {
    if (images.length <= 1) return;
    
    if (autoScrollIntervals.current[providerId]) {
      clearInterval(autoScrollIntervals.current[providerId]);
    }
    
    autoScrollIntervals.current[providerId] = setInterval(() => {
      setCurrentImageIndex(prev => ({
        ...prev,
        [providerId]: ((prev[providerId] || 0) + 1) % images.length
      }));
    }, 4000); // Change image every 4 seconds
    
    setAutoScrollEnabled(prev => ({ ...prev, [providerId]: true }));
  }, []);

  const stopAutoScroll = useCallback((providerId) => {
    if (autoScrollIntervals.current[providerId]) {
      clearInterval(autoScrollIntervals.current[providerId]);
      delete autoScrollIntervals.current[providerId];
    }
    setAutoScrollEnabled(prev => ({ ...prev, [providerId]: false }));
  }, []);

  // Cleanup auto-scroll intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(autoScrollIntervals.current).forEach(interval => {
        clearInterval(interval);
      });
    };
  }, []);

  // Start auto-scroll for providers with multiple images
  useEffect(() => {
    if (filteredResults.length > 0) {
      filteredResults.forEach(provider => {
        if (provider.images && provider.images.length > 1) {
          startAutoScroll(provider.id, provider.images);
        }
      });
    }
    
    return () => {
      // Stop all auto-scroll when component unmounts or results change
      filteredResults.forEach(provider => {
        stopAutoScroll(provider.id);
      });
    };
  }, [filteredResults]); // FIXED: Removed startAutoScroll, stopAutoScroll from deps to prevent infinite loop

  // Fetch categories from backend
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const categoriesResponse = await homeAPI.getCategories();
        console.log('🔍 Categories response:', categoriesResponse);
        
        if (categoriesResponse.data && categoriesResponse.data.categories) {
          console.log('✅ Setting categories:', categoriesResponse.data.categories);
          setCategories(categoriesResponse.data.categories);
        } else {
          console.log('❌ No categories data found in response');
          setCategories([]);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Read search query from URL parameters and set initial search
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const queryParam = urlParams.get('q') || urlParams.get('query') || urlParams.get('search') || '';
    const categoryParam = urlParams.get('category');
    const categoriesParam = urlParams.get('categories');
    const tabParam = urlParams.get('tab');
    
    console.log(`🔍 [URL SYNC] URL parameters: query=${queryParam}, categories=${categoriesParam || categoryParam}, tab=${tabParam}`);
    
    // Only update searchQuery if it's different from current value (prevents infinite loop)
    setSearchQuery(prevQuery => {
      if (prevQuery !== queryParam) {
        console.log(`🔍 [URL SYNC] Updating searchQuery: "${prevQuery}" → "${queryParam}"`);
        return queryParam;
      }
      return prevQuery;
    });
    
    // Handle both single category and multiple categories
    if (categoriesParam) {
      const cats = categoriesParam.split(',').map(c => c.trim()).filter(Boolean);
      setSelectedCategories(prevCats => {
        const catsStr = cats.sort().join(',');
        const prevStr = prevCats.sort().join(',');
        if (catsStr !== prevStr) {
          console.log(`🔍 [URL SYNC] Updating categories: ${prevStr} → ${catsStr}`);
          return cats;
        }
        return prevCats;
      });
    } else if (categoryParam) {
      setSelectedCategories(prev => prev.length === 1 && prev[0] === categoryParam ? prev : [categoryParam]);
    } else {
      setSelectedCategories(prev => prev.length === 0 ? prev : []);
    }
    
    if (tabParam && (tabParam === 'salon' || tabParam === 'pros')) {
      setActiveTab(prev => prev === tabParam ? prev : tabParam);
    }
  }, [location.search]);

  // Categories will be fetched from backend

  // State for API data
  const [results, setResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Helper function to extract provider categories from services
  const getProviderCategories = (provider) => {
    console.log('🔍 Provider data for categories:', {
      services: provider.services,
      businessSettings: provider.businessSettings,
      serviceCategories: provider.businessSettings?.serviceCategories
    });
    
    let categories = [];
    
    // Priority 1: Extract unique category names from services array
    if (provider.services && Array.isArray(provider.services) && provider.services.length > 0) {
      // Extract unique categories from services
      const serviceCategories = provider.services
        .map(service => {
          // Handle different possible field names for category
          if (typeof service === 'string') return service;
          if (service.category) return service.category;
          if (service.categoryId?.name) return service.categoryId.name;
          if (service.serviceCategory) return service.serviceCategory;
          if (service.categoryName) return service.categoryName;
          return null;
        })
        .filter(Boolean); // Remove null/undefined values
      
      categories = [...new Set(serviceCategories)]; // Remove duplicates
      console.log(`🔍 Extracted ${categories.length} unique categories from services:`, categories);
    }
    // Priority 2: Fallback to businessSettings.serviceCategories
    else if (provider.businessSettings?.serviceCategories && Array.isArray(provider.businessSettings.serviceCategories) && provider.businessSettings.serviceCategories.length > 0) {
      categories = [...new Set(provider.businessSettings.serviceCategories)];
      console.log(`🔍 Using businessSettings.serviceCategories:`, categories);
    }
    // Priority 3: Fallback to categories field
    else if (provider.categories && Array.isArray(provider.categories) && provider.categories.length > 0) {
      categories = [...new Set(provider.categories)];
      console.log(`🔍 Using categories field:`, categories);
    }
    
    // Filter out non-category items and undefined/null values
    const validCategories = categories.filter(cat => 
      cat && 
      typeof cat === 'string' &&
      cat.trim().length > 0 &&
      !['Offers', 'Combos', 'offers', 'combos', 'Services', 'services'].includes(cat.toLowerCase())
    );
    
    console.log('✅ Final filtered categories:', validCategories);
    return validCategories;
  };

  // Helper function to fetch services for a provider
  const fetchProviderServices = async (providerId) => {
    try {
      const response = await providerAPI.getProviderServices(providerId);
      const services = response.data?.services || response.message?.services || [];
      
      // Extract unique category names from services
      const categories = services.map(service => {
        // Handle different possible field names for category
        if (service.category) return service.category;
        if (service.categoryId?.name) return service.categoryId.name;
        if (service.serviceCategory) return service.serviceCategory;
        if (service.categoryName) return service.categoryName;
        return null;
      }).filter(Boolean);
      
      // Remove duplicates and filter out non-category items
      const uniqueCategories = [...new Set(categories)].filter(cat => 
        cat && 
        typeof cat === 'string' &&
        cat.trim().length > 0 &&
        !['Offers', 'Combos', 'offers', 'combos', 'Services', 'services'].includes(cat.toLowerCase())
      );
      
      console.log(`🔍 Provider ${providerId} services:`, services.length, 'categories found:', uniqueCategories);
      return uniqueCategories;
    } catch (error) {
      console.error(`Error fetching services for provider ${providerId}:`, error);
      return [];
    }
  };

  // Helper function to get primary category for display
  const getPrimaryCategory = (provider) => {
    const categories = getProviderCategories(provider);
    return categories.length > 0 ? categories[0] : 'No categories';
  };

  // Helper function to get results breakdown by provider type
  const getResultsBreakdown = () => {
    if (!Array.isArray(allResults) || allResults.length === 0) {
      return { salon: 0, pros: 0 };
    }

    // Apply search and category filters to get filtered results
    let filtered = [...allResults];

    // Apply search filter with improved multi-word and partial matching
    if (searchQuery.trim()) {
      const searchWords = searchQuery.toLowerCase().trim().split(/\s+/).filter(word => word.length > 0);
      console.log(`🔍 Searching for words:`, searchWords);
      
      filtered = filtered.filter(provider => {
        const providerName = provider.name?.toLowerCase() || '';
        const providerServices = provider.services || [];
        const providerCategory = provider.category?.toLowerCase() || '';
        
        // First try exact phrase match
        const searchPhrase = searchQuery.toLowerCase();
        if (providerName.includes(searchPhrase)) {
          console.log(`✅ Exact phrase "${searchQuery}" found in provider name: ${provider.name}`);
          return true;
        }
        
        // Then try individual word matches - require at least one word to match
        const matchedWords = searchWords.filter(searchWord => {
          // Check provider name
          if (providerName.includes(searchWord)) {
            console.log(`✅ Word "${searchWord}" found in provider name: ${provider.name}`);
            return true;
          }
          
          // Check provider category
          if (providerCategory.includes(searchWord)) {
            console.log(`✅ Word "${searchWord}" found in provider category: ${provider.category}`);
            return true;
          }
          
          // Check provider services/categories
          const serviceMatch = providerServices.some(service => {
            if (!service) return false;
            
            if (typeof service === 'string') {
              const serviceLower = service.toLowerCase();
              if (serviceLower.includes(searchWord)) {
                console.log(`✅ Word "${searchWord}" found in service: ${service}`);
                return true;
              }
            }
            
            if (typeof service === 'object') {
              const serviceName = service.name?.toLowerCase() || '';
              const serviceDescription = service.description?.toLowerCase() || '';
              const serviceCategory = service.category?.toLowerCase() || '';
              
              if (serviceName.includes(searchWord) || 
                  serviceDescription.includes(searchWord) || 
                  serviceCategory.includes(searchWord)) {
                console.log(`✅ Word "${searchWord}" found in service object: ${service.name || service.category}`);
                return true;
              }
            }
            
            return false;
          });
          
          if (serviceMatch) return true;
          
          // Check individual service names if available (backend provides this)
          if (provider.serviceNames && Array.isArray(provider.serviceNames)) {
            const serviceNameMatch = provider.serviceNames.some(serviceName => 
              serviceName.toLowerCase().includes(searchWord)
            );
            if (serviceNameMatch) {
              console.log(`✅ Word "${searchWord}" found in serviceNames: ${provider.name}`);
              return true;
            }
          }
          
          // Check service descriptions if available (backend provides this)
          if (provider.serviceDescriptions && Array.isArray(provider.serviceDescriptions)) {
            const serviceDescMatch = provider.serviceDescriptions.some(description => 
              description.toLowerCase().includes(searchWord)
            );
            if (serviceDescMatch) {
              console.log(`✅ Word "${searchWord}" found in serviceDescriptions: ${provider.name}`);
              return true;
            }
          }
          
          console.log(`❌ Word "${searchWord}" not found in provider: ${provider.name}`);
          return false;
        });
        
        // Return true if at least one word matches
        return matchedWords.length > 0;
      });
      
      console.log(`🔍 Search results: ${filtered.length} providers match "${searchQuery}"`);
    }

    // Apply category filter (using partial match to match backend behavior)
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(provider => {
        // Use partial matching (case-insensitive) instead of exact match
        const hasMatchingService = provider.services && provider.services.some(service => {
          if (!service) return false;
          
          const serviceLower = (typeof service === 'string' ? service : service.category || service.name || '').toLowerCase();
          
          // Check if ANY selected category is contained in this service (partial match)
          return selectedCategories.some(selectedCat => {
            const selectedCatLower = selectedCat.toLowerCase();
            // Bidirectional partial match: "Hair Colour" matches "Hair Coloring"
            return serviceLower.includes(selectedCatLower) || selectedCatLower.includes(serviceLower);
          });
        });
        
        return hasMatchingService;
      });
    }

    // Count by provider type - check multiple possible field names
    const breakdown = {
      salon: filtered.filter(p => {
        const rawType = p.providerType || p.type || p.businessType;
        const normalizedType = rawType?.toLowerCase();
        return normalizedType === 'salon';
      }).length,
      pros: filtered.filter(p => {
        const rawType = p.providerType || p.type || p.businessType;
        const normalizedType = rawType?.toLowerCase();
        return normalizedType === 'pros';
      }).length
    };

    console.log(`📊 Results breakdown: ${breakdown.salon} salons, ${breakdown.pros} pros (from ${filtered.length} filtered results)`);
    
    // Debug: Log first few providers to see their structure
    if (filtered.length > 0) {
      console.log(`🔍 First provider structure:`, {
        name: filtered[0].name,
        providerType: filtered[0].providerType,
        type: filtered[0].type,
        businessType: filtered[0].businessType,
        rawData: filtered[0]
      });
    }
    
    return breakdown;
  };

  // Handle category selection and update URL
  const handleCategoryToggle = (categoryName) => {
    const newCategories = selectedCategories.includes(categoryName)
      ? selectedCategories.filter(cat => cat !== categoryName)
      : [...selectedCategories, categoryName];
    
    setSelectedCategories(newCategories);
    
    // Update URL to reflect selected categories
    const currentParams = new URLSearchParams(location.search);
    if (newCategories.length > 0) {
      currentParams.set('categories', newCategories.join(','));
      currentParams.delete('category'); // Remove singular parameter if exists
    } else {
      currentParams.delete('categories'); // Remove plural parameter
      currentParams.delete('category'); // Remove singular parameter too!
    }
    
    navigate(`/search-results?${currentParams.toString()}`, { replace: true });
  };

  // Handle provider type change - REMOVED (using new tab logic)

  // Get user location from localStorage (set by home screen)
  useEffect(() => {
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation) {
      try {
        setUserLocation(JSON.parse(savedLocation));
      } catch (error) {
        console.error('Error parsing user location:', error);
      }
    }
  }, []);

  // Client-side filtering function
  const filterResultsLocally = useCallback((overrideActiveTab = null) => {
    const currentActiveTab = overrideActiveTab || activeTab;
    console.log('🔍 filterResultsLocally called with:', {
      searchQuery,
      selectedCategories,
      minRating,
      sortBy,
      providerTypes,
      activeTab: currentActiveTab,
      allResultsLength: allResults.length
    });
    
    if (allResults.length === 0) return;

    let filtered = [...allResults];
    console.log(`🔍 Starting with ${filtered.length} total providers (both salon and pros)`);

    // Filter by search query - improved partial matching
    // Search each word separately across multiple fields with better partial matching
    if (searchQuery.trim()) {
      const searchWords = searchQuery.toLowerCase().trim().split(/\s+/).filter(word => word.length > 0);
      console.log('🔍 Filtering by search words:', searchWords);
      
      filtered = filtered.filter(provider => {
        // Search in provider name, service names, service descriptions, and category names
        const providerName = provider.name?.toLowerCase() || '';
        const providerServices = provider.services || [];
        const providerCategory = provider.category?.toLowerCase() || '';
        
        // Check if ANY search word is found in any of the searchable fields (OR logic)
        const anyWordMatches = searchWords.some(searchWord => {
          // Check provider name with partial matching
          if (providerName.includes(searchWord)) {
            return true;
          }
          
          // Check category name with partial matching
          if (providerCategory.includes(searchWord)) {
            return true;
          }
          
          // Check services (names and descriptions) with improved partial matching
          const serviceMatch = providerServices.some(service => {
            if (!service) return false;
            
            // Check service name with partial matching
            if (typeof service === 'string' && service.toLowerCase().includes(searchWord)) {
              return true;
            }
            
            // Check service object properties (if service is an object) with partial matching
            if (typeof service === 'object') {
              const serviceName = service.name?.toLowerCase() || '';
              const serviceDescription = service.description?.toLowerCase() || '';
              const serviceCategory = service.category?.toLowerCase() || '';
              
              // Improved partial matching - check if search word is contained in any service field
              return serviceName.includes(searchWord) || 
                     serviceDescription.includes(searchWord) || 
                     serviceCategory.includes(searchWord);
            }
            
            return false;
          });
          
          // Check individual service names if available with partial matching
          if (provider.serviceNames && Array.isArray(provider.serviceNames)) {
            const serviceNameMatch = provider.serviceNames.some(serviceName => 
              serviceName.toLowerCase().includes(searchWord)
            );
            if (serviceNameMatch) return true;
          }
          
          // Check service descriptions if available with partial matching
          if (provider.serviceDescriptions && Array.isArray(provider.serviceDescriptions)) {
            const serviceDescMatch = provider.serviceDescriptions.some(description => 
              description.toLowerCase().includes(searchWord)
            );
            if (serviceDescMatch) return true;
          }
          
          if (serviceMatch) {
            return true;
          }
          
          return false;
        });
        
        // Log for debugging
        if (anyWordMatches) {
          console.log('✅ Provider matches search:', provider.name, {
            searchWords,
            providerName,
            providerServices: providerServices.map(s => typeof s === 'string' ? s : s.name || s.category),
            providerCategory
          });
        }
        
        return anyWordMatches;
      });
      
      console.log(`🔍 Search words "${searchWords.join(', ')}" filtered ${allResults.length} providers down to ${filtered.length} results`);
    } else {
      console.log('🔍 No search query - showing all providers');
    }

    // Filter by available provider types (controlled by checkboxes) AND active tab
    console.log(`🔍 CURRENT STATE:`, {
      availableProviderTypes: providerTypes,
      activeTab: currentActiveTab
    });
    
    // Note: Available provider types (checkboxes) only control which tabs are visible,
    // not which providers are shown. Each tab shows only its specific provider type.
    
    // Then filter by active tab (which provider type to show)
    const beforeTabFilter = filtered.length;
    console.log(`🔍 Filtering by ACTIVE TAB: ${currentActiveTab}`);
    
    filtered = filtered.filter(provider => {
      // Enhanced debugging for each provider
      console.log(`🔍 TAB FILTERING PROVIDER: ${provider.name}`, {
        activeTab: provider.activeTab,
        type: provider.type,
        businessType: provider.businessType,
        providerType: provider.providerType,
        allStringFields: Object.keys(provider).filter(key => typeof provider[key] === 'string').map(key => ({ [key]: provider[key] }))
      });
      
      const tabMatch = provider.providerType === currentActiveTab || provider.type === currentActiveTab;
      
      
      if (tabMatch) {
        console.log(`✅ Provider ${provider.name} (${provider.type || provider.providerType}) matches active tab: ${currentActiveTab}`);
      } else {
        console.log(`❌ Provider ${provider.name} (${provider.type || provider.providerType}) does NOT match active tab: ${currentActiveTab}`);
      }
      
      return tabMatch;
    });
    
    console.log(`🔍 Active tab filter (${currentActiveTab}): ${beforeTabFilter} -> ${filtered.length} providers`);

    // Filter by selected categories (using partial match to match backend behavior)
    if (selectedCategories.length > 0) {
      const beforeCategoryFilter = filtered.length;
      console.log(`🔍 Filtering by categories: ${selectedCategories.join(', ')}`);
      
      filtered = filtered.filter(provider => {
        // Use partial matching (case-insensitive) instead of exact match
        // This matches backend behavior where "Hair Colour" finds "Hair Coloring", "Hair Color", etc.
        const hasMatchingService = provider.services && provider.services.some(service => {
          if (!service) return false;
          
          const serviceLower = (typeof service === 'string' ? service : service.category || service.name || '').toLowerCase();
          
          // Check if ANY selected category is contained in this service (partial match)
          return selectedCategories.some(selectedCat => {
            const selectedCatLower = selectedCat.toLowerCase();
            // Bidirectional partial match: "Hair Colour" matches "Hair Coloring" and vice versa
            return serviceLower.includes(selectedCatLower) || selectedCatLower.includes(serviceLower);
          });
        });
        
        if (hasMatchingService) {
          console.log(`✅ Provider ${provider.name} matches categories:`, selectedCategories);
        } else {
          console.log(`❌ Provider ${provider.name} does NOT match categories. Services:`, provider.services);
        }
        
        return hasMatchingService;
      });
      
      console.log(`🔍 Category filter: ${beforeCategoryFilter} -> ${filtered.length} providers`);
      console.log(`🔍 Providers after category filtering:`, filtered.map(p => ({ name: p.name, type: p.type, services: p.services })));
    }

    // Apply rating filter
    if (minRating > 0) {
      const beforeRatingFilter = filtered.length;
      filtered = filtered.filter(provider => provider.rating >= minRating);
      console.log(`🔍 Rating filter (>=${minRating}): ${beforeRatingFilter} -> ${filtered.length} providers`);
    }

    // Apply sorting
    if (sortBy) {
      console.log(`🔍 Sorting by: ${sortBy}`);
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'rating-high':
            return (b.rating || 0) - (a.rating || 0);
          case 'rating-low':
            return (a.rating || 0) - (b.rating || 0);
          case 'name-asc':
            return (a.name || '').localeCompare(b.name || '');
          case 'name-desc':
            return (b.name || '').localeCompare(a.name || '');
          default:
            return 0;
        }
      });
    }

    console.log(`🔍 Final filtered results: ${filtered.length} providers`);
    console.log(`🔍 Filtered providers:`, filtered.map(p => ({ name: p.name, type: p.type })));
    setFilteredResults(filtered);
  }, [searchQuery, selectedCategories, minRating, sortBy, activeTab, allResults]);

  // Call filterResultsLocally whenever dependencies change
  useEffect(() => {
    console.log('🔍 [FILTER TRIGGER] Dependencies changed, calling filterResultsLocally');
    filterResultsLocally();
  }, [filterResultsLocally]);

  // Debounced search query for API calls AND update URL
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('🔍 Setting debounced search query:', searchQuery);
      setDebouncedSearchQuery(searchQuery);
      
      // Update URL to reflect current search query
      const currentParams = new URLSearchParams(location.search);
      const currentUrlQuery = currentParams.get('q') || '';
      
      // Only update URL if search query actually changed
      if (searchQuery.trim() && searchQuery.trim() !== currentUrlQuery) {
        currentParams.set('q', searchQuery.trim());
        navigate(`/search-results?${currentParams.toString()}`, { replace: true });
        console.log('🔍 Updated URL: ?q=' + searchQuery.trim());
      } else if (!searchQuery.trim() && currentUrlQuery) {
        currentParams.delete('q');
        navigate(`/search-results?${currentParams.toString()}`, { replace: true });
        console.log('🔍 Cleared search query from URL');
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery, navigate]);

  // Fetch search results using combined search API
  const fetchSearchResults = async () => {
    try {
      setLoading(true);
      setError(null);
      setPage(1);
      
      const params = {
        // Categories are filtered on frontend (not sent to backend)
        // This prevents API calls when categories change
        minRating: minRating > 0 ? minRating : undefined,
        sortBy: sortBy,
        page: 1,
        limit: 100
      };
      
      console.log('🔍 [SEARCH RESULTS] Fetching search results with query:', debouncedSearchQuery, 'params:', params);
      
      // FIXED: Pass query as first argument and params as second argument
      // searchAPI.searchProviders signature is: (query, params = {})
      const response = await searchAPI.searchProviders(debouncedSearchQuery, params);
      console.log('🔍 Search response:', response);
        
      // Extract providers from response
      let providers = Array.isArray(response.data?.providers) ? response.data.providers : 
                       Array.isArray(response.message?.providers) ? response.message.providers : 
                       Array.isArray(response.data) ? response.data : 
                       Array.isArray(response) ? response : 
                       Array.isArray(response.providers) ? response.providers : 
                       Array.isArray(response.data?.data?.providers) ? response.data.data.providers : [];
        
      console.log('✅ Processed providers:', providers);
      console.log(`🔍 Backend returned ${providers.length} providers for search query: "${debouncedSearchQuery}"`);
      
      // Log first provider to see the structure
      if (providers.length > 0) {
        console.log('🔍 First provider structure:', JSON.stringify(providers[0], null, 2));
      }
      
      // Transform the data to match frontend expectations
      const transformedResults = providers.map(provider => {
          const rawType = (provider.providerType || provider.type || provider.businessType || '').toLowerCase();
          const normalizedType = (rawType === 'pro' ? 'pros' : rawType) || 'salon';
          
          // Debug transformation
          console.log(`🔄 TRANSFORMING PROVIDER: ${provider.businessName || provider.name}`, {
            originalProviderType: provider.providerType,
            originalType: provider.type,
            originalBusinessType: provider.businessType,
            rawType: rawType,
            normalizedType: normalizedType
          });
          
          // Build robust images array prioritizing photoGallery
          const gallery = provider?.businessSettings?.photoGallery || [];
          const imagesArr = Array.isArray(provider.images) ? [...provider.images] : [];
          if (Array.isArray(gallery) && gallery.length > 0) {
            gallery.forEach((img) => imagesArr.push(img));
          }
          if (imagesArr.length === 0 && provider.logoUrl) imagesArr.push(provider.logoUrl);
          if (imagesArr.length === 0 && provider.profilePhotoUrl) imagesArr.push(provider.profilePhotoUrl);
          
          // Extract service categories from backend response
          // Backend now provides: provider.services (categories), provider.serviceNames, provider.serviceDescriptions
          const categories = getProviderCategories(provider);
          
          const transformed = {
            id: provider._id,
            name: provider.businessName || provider.name,
            type: normalizedType,
            businessType: provider.businessType,
            providerType: provider.providerType,
            images: imagesArr,
            featured: provider.isFeatured || provider.featured,
            distance: provider.distance || 'N/A',
            category: categories.length > 0 ? categories[0] : 'No categories',
            rating: provider.averageRating || provider.rating || 0,
            reviewCount: provider.reviewCount || provider.totalReviews || 0,
            services: categories, // Backend already provides this!
            serviceNames: provider.serviceNames || [], // Service names from backend
            serviceDescriptions: provider.serviceDescriptions || [] // Service descriptions from backend
          };
          
          console.log(`🔄 TRANSFORMED ${transformed.name}:`, {
            'services (categories)': transformed.services,
            'serviceNames': transformed.serviceNames?.length || 0,
            'serviceDescriptions': transformed.serviceDescriptions?.length || 0,
            'reviewCount': transformed.reviewCount,
            'rating': transformed.rating
          });
          
          return transformed;
        });
        
      console.log('✅ Transformed results ready (no additional service fetching needed):', transformedResults.length);
      setAllResults(transformedResults); // Store all results - backend already includes services!
      // Check if there are more results to load
      setHasMore(providers.length === 100); // If we got 100, there might be more
    } catch (err) {
      console.error('Error fetching search results:', err);
      setError('Failed to load search results. Please try again.');
      setAllResults([]);
      setFilteredResults([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch results ONLY when search query changes (categories filtered on frontend)
  useEffect(() => {
    // Fetch results whenever search query changes
    console.log('🔍 [FETCH TRIGGER] debouncedSearchQuery changed:', debouncedSearchQuery);
    fetchSearchResults();
  }, [debouncedSearchQuery]); // Categories are filtered client-side, no API call needed

  // Trigger filtering when initial data is loaded
  useEffect(() => {
    console.log('🚀 Initial data loaded, checking if filtering should be triggered...');
    console.log('🚀 State check:', { allResultsLength: allResults.length, providerTypes });
    
    if (allResults.length > 0) {
      console.log('🚀 Triggering initial filtering...');
      filterResultsLocally();
    }
  }, [allResults, providerTypes]);

  // Auto-switch to first available tab if current tab becomes unavailable
  useEffect(() => {
    if (providerTypes.length > 0 && !providerTypes.includes(activeTab)) {
      console.log(`🔍 Auto-switching from ${activeTab} to ${providerTypes[0]} (tab no longer available)`);
      setActiveTab(providerTypes[0]);
    }
  }, [providerTypes, activeTab]);

  // Note: filterResultsLocally is called directly when needed (e.g., tab clicks, filter changes)

  // Infinite scroll functionality - Not needed since we're showing all providers at once
  // useEffect(() => {
  //   const handleScroll = () => {
  //     if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1000) {
  //       if (hasMore && !loading) {
  //         loadMore();
  //       }
  //     }
  //   };

  //   window.addEventListener('scroll', handleScroll);
  //   return () => window.removeEventListener('scroll', handleScroll);
  // }, [hasMore, loading]);

  // Load more results (Load More button)
  const loadMore = async () => {
    if (loading || !hasMore) return;
    
    try {
      setLoading(true);
      const nextPage = page + 1;
      
      const params = {
        // Don't filter by type in backend - we'll get both types and filter client-side
        // type: activeTab,
        categories: selectedCategories.length > 0 ? selectedCategories : undefined,
        minRating: minRating > 0 ? minRating : undefined,
        // Distance filter removed for now
        sortBy: sortBy,
        page: nextPage,
        limit: 40, // Increased limit to get more data for both types
        // Location removed for now
        // latitude: userLocation?.latitude,
        // longitude: userLocation?.longitude
      };
      
      console.log('🔍 Loading more providers with query:', debouncedSearchQuery, 'params:', params);
      // FIXED: Use searchAPI for consistency (matches main search call)
      const response = await searchAPI.searchProviders(debouncedSearchQuery, params);
      console.log('🔍 Load more response:', response);
      
              // Ensure we always have an array - check response.data.providers first (backend structure)
        const newResults = Array.isArray(response.data?.providers) ? response.data.providers :
                          Array.isArray(response.message?.providers) ? response.message.providers :
                          Array.isArray(response.data) ? response.data :
                          Array.isArray(response) ? response :
                          Array.isArray(response.providers) ? response.providers : [];
      
      if (newResults.length > 0) {
        // Transform the new results to match frontend expectations
        const transformedNewResults = newResults.map(provider => {
          const rawType = (provider.providerType || provider.type || provider.businessType || '').toLowerCase();
          const normalizedType = (rawType === 'pro' ? 'pros' : rawType) || 'salon';
          const categories = getProviderCategories(provider);
          
          const transformed = {
            id: provider._id,
            name: provider.businessName || provider.name,
            type: normalizedType,
            businessType: provider.businessType,
            providerType: provider.providerType,
            images: provider.images || provider.businessSettings?.photoGallery || [],
            featured: provider.isFeatured || provider.featured,
            distance: provider.distance || 'N/A',
            category: categories.length > 0 ? categories[0] : 'No categories',
            rating: provider.averageRating || provider.rating || 0,
            reviewCount: provider.reviewCount || 0,
            services: categories, // Backend already provides this
            serviceNames: provider.serviceNames || [],
            serviceDescriptions: provider.serviceDescriptions || []
          };
          
          console.log('🔄 Load more transformed provider:', {
            name: transformed.name,
            type: transformed.type,
            servicesCount: transformed.services?.length || 0,
            rating: transformed.rating
          });
          
          return transformed;
        });

        // Add new results to existing results (no need to fetch services - backend already provides them)
        setAllResults(prev => [...prev, ...transformedNewResults]);
        setFilteredResults(prev => [...prev, ...transformedNewResults]);
        setPage(nextPage);
        // Check if there are more results to load
        setHasMore(newResults.length === 20);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading more providers:', err);
      setError('Failed to load more providers.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-scroll images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => {
        const newIndex = {};
        if (Array.isArray(filteredResults)) {
          filteredResults.forEach(result => {
            if (result.images && result.images.length > 1) {
              newIndex[result.id] = ((prev[result.id] || 0) + 1) % result.images.length;
            }
          });
        }
        return newIndex;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [filteredResults]);

  // Dynamic placeholder
  useEffect(() => {
    const placeholders = [
      "Search for Provider",
      "Search for Haircut", 
      "Search for Facial",
      "Search for Waxing",
      "Search for Hair Treatment"
    ];
    
    const interval = setInterval(() => {
      setPlaceholderIndex(prev => (prev + 1) % placeholders.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Handle image dot click
  const handleImageDotClick = (resultId, imageIndex) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [resultId]: imageIndex
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedCategories([]);
    setSearchQuery('');
    setDistanceType('entire-city');
    setDistanceValue(10);
    setMinRating(0);
    setSortBy('rating-high');
  };

  // Display current location info
  const getLocationInfo = () => {
    if (userLocation) {
      // Show full address if available, otherwise show city or fallback
      if (userLocation.fullAddress) {
        return `📍 ${userLocation.fullAddress}`;
      } else if (userLocation.address) {
        return `📍 ${userLocation.address}`;
      } else if (userLocation.city) {
        return `📍 ${userLocation.city}`;
      } else {
        return '📍 Current Location';
      }
    }
    return '📍 Location not set';
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#1a237e' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            NISTYLEZ Experts
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ 
        flexGrow: 1,
        p: { xs: 2, md: 3 },
        width: '100%',
        backgroundColor: '#f5f5f5',
        py: 2,
        pb: { xs: 8, md: 8 } // Add bottom padding to prevent content from being hidden behind bottom navigation
      }}>
                 {/* Search Bar with Filter */}
         <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
           <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
             <Search sx={{ flex: 1 }}>
               <SearchIconWrapper
                 onClick={() => {
                   console.log('🔍 Search icon clicked, triggering search');
                   fetchSearchResults();
                 }}
                 sx={{ cursor: 'pointer' }}
               >
                 <SearchIcon />
               </SearchIconWrapper>
                              <StyledInputBase
                  placeholder={[
                    "Search for Provider",
                    "Search for Haircut", 
                    "Search for Facial",
                    "Search for Waxing",
                    "Search for Hair Treatment"
                  ][placeholderIndex]}
                  value={searchQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    console.log('🔍 Search input changed:', value);
                    setSearchQuery(value);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      console.log('🔍 Enter key pressed, triggering search');
                      fetchSearchResults();
                    }
                  }}
                  sx={{ width: '100%' }}
                />
                {searchQuery && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{ 
                      position: 'absolute', 
                      right: 8, 
                      color: 'rgba(0,0,0,0.5)',
                      '&:hover': { color: 'rgba(0,0,0,0.8)' }
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                )}
             </Search>
             <IconButton
               onClick={() => setFilterDialogOpen(true)}
               sx={{ 
                 backgroundColor: '#1a237e',
                 color: 'white',
                 '&:hover': { backgroundColor: '#0d47a1' }
               }}
             >
               <FilterListIcon />
             </IconButton>
           </Box>
           {/* Location Info */}
           <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
             <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
               {getLocationInfo()}
             </Typography>
             {distanceType === 'distance' && (
               <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                 • Within {distanceValue}km radius
               </Typography>
             )}
                           {distanceType === 'entire-city' && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                  • Entire city (no distance limit)
                </Typography>
              )}
           </Box>
           
           {/* Search Results Counter */}
           {searchQuery.trim() && (
             <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
               <Typography variant="caption" color="primary" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                 🔍 Search: "{searchQuery}" - {filteredResults.length} results found
               </Typography>
             </Box>
           )}
           
           {/* Search Help Text */}
           <Box sx={{ mt: 1 }}>
             <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
               💡 Search by provider name, type, services, or categories
             </Typography>
           </Box>
         </Paper>

                 {/* Categories Section - Fetched from Backend */}
         <Box sx={{ mb: 4 }}>
           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
             <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
               Select Categories
             </Typography>
             {selectedCategories.length > 0 && (
               <Button
                 startIcon={<ClearIcon />}
                 onClick={clearFilters}
                 size="small"
                 sx={{ color: '#666' }}
               >
                 Clear All
               </Button>
             )}
           </Box>
           
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'row',
            gap: { xs: 2, sm: 2.5 },
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            pb: 2,
            pt: { xs: 1, sm: 1 },
            px: { xs: 1, sm: 0 },
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a8a8a8' }
          }}>
             {categoriesLoading ? (
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 4 }}>
                 <CircularProgress />
               </Box>
            ) : categories.length > 0 ? (
              categories.map((category, index) => (
                <Card 
                  key={index}
                  onClick={() => handleCategoryToggle(category.name)}
                  sx={{ 
                    minWidth: { xs: 120, sm: 130, md: 140 },
                    maxWidth: { xs: 120, sm: 130, md: 140 },
                    height: { xs: 130, sm: 140, md: 150 },
                    
                    // DYNAMIC BACKGROUND - Gold when selected, Blue when not
                    background: selectedCategories.includes(category.name)
                      ? 'linear-gradient(180deg, rgba(255, 245, 200, 0.9) 0%, rgba(255, 215, 0, 0.8) 40%, #FFD700 100%)'
                      : 'linear-gradient(180deg, rgba(230, 242, 255, 0.4) 0%, rgba(200, 228, 255, 0.6) 18%, #2874BA 100%)',
                    
                    borderRadius: { xs: '0 30px 0 0', sm: '0 40px 0 0', md: '0 45px 0 0' },
                    padding: { xs: '8px 8px 14px', sm: '10px 10px 16px', md: '12px 12px 20px' },
                    position: 'relative',
                    
                    // DYNAMIC SHADOW - Stronger when selected
                    boxShadow: selectedCategories.includes(category.name)
                      ? '0 6px 20px rgba(255, 215, 0, 0.4)'
                      : '0 3px 12px rgba(40, 116, 186, 0.18)',
                    
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: selectedCategories.includes(category.name)
                        ? '0 10px 30px rgba(255, 215, 0, 0.5)'
                        : '0 10px 30px rgba(40, 116, 186, 0.35)'
                    }
                  }}
                >
                  {/* Icon at top - INSIDE card */}
                  <Box sx={{ 
                    position: 'absolute',
                    top: { xs: 8, sm: 10, md: 12 },
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: { xs: 52, sm: 58, md: 64 },
                    height: { xs: 52, sm: 58, md: 64 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
                    zIndex: 5
                  }}>
                    {category.iconUrl ? (
                      <img 
                        src={category.iconUrl} 
                        alt={category.name}
                        style={{ 
                          width: '48px', 
                          height: '48px',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: { xs: '2.4rem', sm: '2.7rem', md: '3rem' } }}>
                        {category.icon || '✨'}
                      </Typography>
                    )}
                  </Box>

                  {/* Tick mark - Top Right Corner - ONLY when selected */}
                  {selectedCategories.includes(category.name) && (
                    <Box sx={{ 
                      position: 'absolute',
                      top: { xs: 4, sm: 6, md: 8 },
                      right: { xs: 4, sm: 6, md: 8 },
                      backgroundColor: '#1a237e',
                      borderRadius: '50%',
                      width: { xs: 24, sm: 26, md: 28 },
                      height: { xs: 24, sm: 26, md: 28 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      zIndex: 10
                    }}>
                      <CheckCircleIcon sx={{ color: 'white', fontSize: { xs: 14, sm: 16, md: 18 } }} />
                    </Box>
                  )}
                  
                  {/* Category Name in Middle */}
                  <Typography variant="body2" sx={{ 
                    fontWeight: 700,
                    color: 'white',
                    fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
                    textAlign: 'center',
                    marginTop: 'auto',
                    marginBottom: 'auto',
                    paddingTop: { xs: '50px', sm: '55px', md: '60px' },
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.15)'
                  }}>
                    {category.name}
                  </Typography>
                </Card>
              ))
            ) : (
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 4 }}>
                 <Typography variant="h6" color="text.secondary">
                   No categories available
                 </Typography>
               </Box>
             )}
           </Box>
         </Box>

        {/* Provider Type Toggle */}
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={activeTab}
            exclusive
            onChange={(event, newValue) => {
              console.log(`🔍 TAB CLICKED: ${newValue}`);
              // Tabs only control which view to show, don't change checkbox state
              if (newValue) {
                console.log(`🔍 SETTING ACTIVE TAB FROM ${activeTab} TO ${newValue}`);
                setActiveTab(newValue);
                // Trigger filtering immediately with the new tab value
                // This ensures filtering runs with the correct activeTab value
                console.log(`🔍 MANUALLY TRIGGERING FILTERING FOR TAB: ${newValue}`);
                filterResultsLocally(newValue);
              }
            }}
            sx={{ 
              width: '100%',
              '& .MuiToggleButton-root': {
                flex: 1,
                py: 1.5,
                fontWeight: 'bold',
                textTransform: 'none',
                fontSize: '1rem',
                borderRadius: '12px !important',
                '&.Mui-selected': {
                  backgroundColor: '#1a237e',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#0d47a1'
                  }
                }
              }
            }}
          >
            {providerTypes.includes('salon') && (
              <ToggleButton value='salon'>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                 <Box sx={{ 
                   width: 24, 
                   height: 24, 
                   borderRadius: '50%', 
                   backgroundColor: 'rgba(255,255,255,0.2)',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center'
                 }}>
                   <Typography sx={{ fontSize: '1rem', color: 'inherit' }}>🏢</Typography>
                 </Box>
                 <Typography sx={{ fontWeight: 600 }}>Salon</Typography>
                 {/* Always show result count */}
                 <Box sx={{ 
                   ml: 1, 
                   px: 1.5, 
                   py: 0.5, 
                   backgroundColor: '#1976d2', 
                   borderRadius: '16px',
                   minWidth: '24px',
                   textAlign: 'center',
                   border: '2px solid #ffffff',
                   boxShadow: '0 2px 6px rgba(25, 118, 210, 0.4)'
                 }}>
                   <Typography variant="caption" sx={{ 
                     fontSize: '0.75rem', 
                     fontWeight: 'bold',
                     color: '#ffffff',
                     textShadow: '0 1px 3px rgba(0,0,0,0.5)'
                   }}>
                     {getResultsBreakdown().salon}
                   </Typography>
                 </Box>
               </Box>
             </ToggleButton>
            )}
            {providerTypes.includes('pros') && (
              <ToggleButton value="pros">
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                 <Box sx={{ 
                   width: 24, 
                   height: 24, 
                   borderRadius: '50%', 
                   backgroundColor: 'rgba(255,255,255,0.2)',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center'
                 }}>
                   <Typography sx={{ fontSize: '1rem', color: 'inherit' }}>💇‍♀️</Typography>
                 </Box>
                 <Typography sx={{ fontWeight: 600 }}>Pros</Typography>
                 {/* Always show result count */}
                 <Box sx={{ 
                   ml: 1, 
                   px: 1.5, 
                   py: 0.5, 
                   backgroundColor: '#1976d2', 
                   borderRadius: '16px',
                   minWidth: '24px',
                   textAlign: 'center',
                   border: '2px solid #ffffff',
                   boxShadow: '0 2px 6px rgba(25, 118, 210, 0.4)'
                 }}>
                   <Typography variant="caption" sx={{ 
                     fontSize: '0.75rem', 
                     fontWeight: 'bold',
                     color: '#ffffff',
                     textShadow: '0 1px 3px rgba(0,0,0,0.5)'
                   }}>
                     {getResultsBreakdown().pros}
                   </Typography>
                 </Box>
               </Box>
             </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Box>

        {/* Results Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                         <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
               {activeTab === 'salon' ? 'Salons' : activeTab === 'pros' ? 'Pros' : 'All Providers'} 
               {selectedCategories.length > 0 && ` - ${selectedCategories.join(', ')}`}
             </Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        {/* Current tab results count */}
                        <Box sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 2.5,
                          py: 1.2,
                          backgroundColor: '#1976d2',
                          borderRadius: '25px',
                          boxShadow: '0 3px 10px rgba(25, 118, 210, 0.4)',
                          border: '2px solid #ffffff',
                          minWidth: '120px',
                          justifyContent: 'center'
                        }}>
                          <Typography sx={{
                            fontSize: '0.9rem',
                            fontWeight: 'bold',
                            color: '#ffffff',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                            letterSpacing: '0.5px'
                          }}>
                            {Array.isArray(filteredResults) ? filteredResults.length : 0} {activeTab === 'salon' ? 'salons' : activeTab === 'pros' ? 'pros' : 'providers'} found
                          </Typography>
                        </Box>
                      </Box>
          {!Array.isArray(filteredResults) && (
            <Typography variant="caption" color="error">
              Debug: filteredResults is {typeof filteredResults} - {JSON.stringify(filteredResults).substring(0, 100)}
            </Typography>
          )}
          </Box>

                    {loading && page === 1 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !Array.isArray(filteredResults) ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="error">
                Data format error
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Expected array but got {typeof filteredResults}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {JSON.stringify(filteredResults).substring(0, 200)}
              </Typography>
            </Paper>
          ) : filteredResults.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No {activeTab === 'salon' ? 'salons' : activeTab === 'pros' ? 'pros' : 'providers'} found
              </Typography>
              {/* Show breakdown when there are results in other tabs */}
              {(searchQuery.trim() || selectedCategories.length > 0) && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Results found in other tabs:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getResultsBreakdown().salon} salons found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getResultsBreakdown().pros} pros found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Try switching tabs to see all results
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your search or filters
              </Typography>
            </Paper>
          ) : (
            <>
                             <Grid container spacing={2}>
                                  {Array.isArray(filteredResults) && filteredResults.map((result) => {
                   // Debug logging for rendering
                   console.log(`🎨 RENDERING PROVIDER CARD: ${result.name}`, {
                     name: result.name,
                     type: result.type,
                     providerType: result.providerType,
                     activeTab: activeTab
                   });
                   
                   // Debug logging for images
                   console.log(`🖼️ Provider ${result.name} images:`, {
                     name: result.name,
                     images: result.images,
                     imageCount: result.images?.length || 0,
                     currentIndex: currentImageIndex[result.id] || 0
                   });
                   
                   return (
                   <Grid item xs={12} sm={6} md={4} key={result.id}>
                    <Card 
                      onClick={() => navigate(`/provider/${result.id}`)}
                      sx={{ 
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 6
                      },
                      borderRadius: 2,
                      overflow: 'visible',
                      border: '1px solid #f0f0f0'
                    }}>
                                              <Box sx={{ 
                           position: 'relative', 
                           height: { xs: 180, sm: 200 },
                           maxHeight: { xs: 180, sm: 200 },
                           minHeight: { xs: 180, sm: 200 },
                           overflow: 'hidden',
                           flexShrink: 0
                         }}>
                         <CardMedia
                           component="img"
                           height="100%"
                           image={result.images && result.images.length > 0 ? result.images[currentImageIndex[result.id] || 0] : '/placeholder-salon.jpg'}
                           alt={result.name || 'Provider'}
                           sx={{ 
                             objectFit: 'cover',
                             transition: 'opacity 0.5s ease',
                             width: '100%',
                             height: '100%',
                             maxHeight: '100%',
                             display: 'block'
                           }}
                            onMouseEnter={() => {
                              // Pause auto-scroll on hover
                              if (result.images && result.images.length > 1) {
                                stopAutoScroll(result.id);
                              }
                            }}
                            onMouseLeave={() => {
                              // Resume auto-scroll when mouse leaves
                              if (result.images && result.images.length > 1) {
                                startAutoScroll(result.id, result.images);
                              }
                            }}
                            onError={(e) => {
                              // Fallback to placeholder image if the image fails to load
                              e.target.src = '/placeholder-salon.jpg';
                            }}
                          />
                         {result.featured && (
                           <Chip 
                             label="Featured" 
                             size="small" 
                             sx={{ 
                               position: 'absolute', 
                               top: 8, 
                               left: 8,
                               backgroundColor: '#ffd700',
                               color: '#333',
                               fontWeight: 'bold',
                               fontSize: '0.7rem'
                             }}
                           />
                         )}
                       {/* Provider Type Badge - REMOVED */}
                       {/* Photo Gallery Counter - REMOVED */}
                       
                       {/* Favorite Heart Icon - Top Right */}
                       <IconButton
                         onClick={(e) => {
                           e.stopPropagation();
                           // TODO: Add favorite functionality
                         }}
                         size="small"
                         sx={{ 
                           position: 'absolute', 
                           top: 8, 
                           right: 8,
                           backgroundColor: 'rgba(255,255,255,0.9)',
                           color: '#666',
                           zIndex: 3,
                           '&:hover': { 
                             backgroundColor: 'rgba(255,255,255,1)',
                             color: '#1a237e'
                           }
                         }}
                       >
                         <FavoriteBorderIcon sx={{ fontSize: 18 }} />
                       </IconButton>
                       
                       {/* Auto-scroll indicator */}
                       {result.images && result.images.length > 1 && autoScrollEnabled[result.id] && (
                         <Box
                           sx={{
                             position: 'absolute',
                             top: 8,
                             right: 48,
                             backgroundColor: 'rgba(0,255,0,0.8)',
                             color: 'white',
                             borderRadius: '50%',
                             width: 8,
                             height: 8,
                             zIndex: 2
                           }}
                         />
                       )}
                         
                                                   {/* Image indicators */}
                          {result.images && result.images.length > 1 && (
                            <Box sx={{ 
                              position: 'absolute', 
                              bottom: 8, 
                              left: '50%', 
                              transform: 'translateX(-50%)',
                              display: 'flex', 
                              gap: 0.5 
                            }}>
                              {result.images.map((_, index) => (
                                <Box
                                  key={index}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleImageDotClick(result.id, index);
                                  }}
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: (currentImageIndex[result.id] || 0) === index 
                                      ? 'white' 
                                      : 'rgba(255,255,255,0.5)',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      backgroundColor: (currentImageIndex[result.id] || 0) === index 
                                        ? 'white' 
                                        : 'rgba(255,255,255,0.7)'
                                    }
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                       </Box>
                       
                      <CardContent sx={{ 
                        p: { xs: 1.5, sm: 1.5 }, 
                        display: 'flex', 
                        flexDirection: 'column',
                        position: 'relative',
                        zIndex: 1,
                        flex: '1 0 auto',
                        '&:last-child': { pb: { xs: 1.5, sm: 1.5 } }
                      }}>
                        {/* Provider Name */}
                        <Box sx={{ mb: { xs: 0.5, sm: 0.5 } }}>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 'bold', 
                              fontSize: { xs: '0.85rem', sm: '0.9rem' },
                              lineHeight: 1.2,
                              cursor: 'pointer',
                              wordBreak: 'break-word',
                              '&:hover': {
                                color: '#1976d2',
                                textDecoration: 'underline'
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/provider/${result.id}`);
                            }}
                          >
                            {result.name}
                          </Typography>
                        </Box>
                        
                        {/* Rating, Distance, and Provider Type Chip on same line */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: result.services && result.services.length > 0 ? { xs: 0.5, sm: 0.5 } : 0,
                          flexWrap: 'wrap',
                          gap: { xs: 0.5, sm: 1 }
                        }}>
                          {/* Rating */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            flexShrink: 0
                          }}>
                            <StarIcon sx={{ 
                              color: '#ffc107', 
                              fontSize: { xs: 14, sm: 16 }, 
                              mr: 0.5,
                              flexShrink: 0
                            }} />
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontWeight: 'bold',
                                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                mr: 0.5,
                                flexShrink: 0
                              }}
                            >
                              {result.rating > 0 ? result.rating.toFixed(1) : '0'}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                fontSize: { xs: '0.7rem', sm: '0.8rem' }, 
                                fontWeight: 500,
                                flexShrink: 0
                              }}
                            >
                              ({result.reviewCount > 0 ? result.reviewCount : '0'})
                            </Typography>
                          </Box>
                          
                          {/* Distance */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            flexShrink: 0
                          }}>
                            <LocationIcon sx={{ 
                              color: '#1976d2', 
                              fontSize: { xs: 14, sm: 16 }, 
                              mr: 0.5,
                              flexShrink: 0
                            }} />
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                fontSize: { xs: '0.75rem', sm: '0.85rem' }, 
                                fontWeight: 500
                              }}
                            >
                              {result.distance}
                            </Typography>
                          </Box>
                          
                          {/* Provider Type Chip */}
                          <Chip
                            label={(() => {
                              const activeTab = result.activeTab || result.type;
                              return activeTab === 'pros' ? 'Pros' : activeTab === 'salon' ? 'Salon' : 'Unknown';
                            })()}
                            size="small"
                            sx={{
                              fontSize: { xs: '0.6rem', sm: '0.65rem' },
                              height: { xs: 18, sm: 20 },
                              fontWeight: 'bold',
                              flexShrink: 0,
                              backgroundColor: (() => {
                                const activeTab = result.activeTab || result.type;
                                return activeTab === 'pros' ? '#dc004e' : activeTab === 'salon' ? '#1976d2' : '#666';
                              })(),
                              color: 'white',
                              '&:hover': {
                                backgroundColor: (() => {
                                  const activeTab = result.activeTab || result.type;
                                  return activeTab === 'pros' ? '#b71c1c' : activeTab === 'salon' ? '#1565c0' : '#555';
                                })()
                              }
                            }}
                          />
                        </Box>
                          
                          {/* Category Chips - Click to filter client-side only */}
                          {result.services && result.services.length > 0 && (
                            <Box sx={{ 
                              display: 'flex', 
                              flexWrap: 'wrap', 
                              gap: { xs: 0.5, sm: 0.5 }, 
                              alignItems: 'flex-start'
                            }}>
                              {result.services
                                .filter(service => !['Offers', 'Combos', 'offers', 'combos'].includes(service)) // Filter out non-categories
                                .slice(0, 3) // Limit to 3 categories
                                .map((service, idx) => (
                                  <Chip 
                                    key={idx} 
                                    label={service} 
                                    size="small" 
                                    variant={selectedCategories.includes(service) ? "filled" : "outlined"}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Client-side filtering only - no backend API calls
                                      handleCategoryToggle(service);
                                    }}
                                    sx={{ 
                                      fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                      borderColor: selectedCategories.includes(service) ? '#1565c0' : '#1976d2',
                                      color: selectedCategories.includes(service) ? 'white' : '#1976d2',
                                      height: 'auto',
                                      minHeight: { xs: 18, sm: 20 },
                                      backgroundColor: selectedCategories.includes(service) 
                                        ? '#1976d2' 
                                        : 'rgba(25, 118, 210, 0.08)',
                                      cursor: 'pointer',
                                      '& .MuiChip-label': {
                                        padding: '2px 8px',
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.2
                                      },
                                      '&:hover': {
                                        backgroundColor: selectedCategories.includes(service)
                                          ? '#1565c0'
                                          : 'rgba(25, 118, 210, 0.12)',
                                        borderColor: '#1565c0'
                                      }
                                    }}
                                  />
                                ))
                              }
                            </Box>
                          )}
                       </CardContent>
                                            </Card>
                   </Grid>
                 );
                 })}
                              </Grid>
               
                                               {/* Load More Button */}
                {hasMore && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Button
                      onClick={loadMore}
                      disabled={loading}
                      variant="outlined"
                      sx={{
                        borderColor: '#1a237e',
                        color: '#1a237e',
                        '&:hover': {
                          borderColor: '#0d47a1',
                          backgroundColor: '#f5f5f5'
                        },
                        '&:disabled': {
                          borderColor: '#ccc',
                          color: '#ccc'
                        }
                      }}
                    >
                      {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          Loading...
                        </Box>
                      ) : (
                        'Load More'
                      )}
                    </Button>
                  </Box>
                )}
                
                {/* Loading indicator for initial load */}
                {loading && page === 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
            </>
          )}
        </Box>
      </Box>

             {/* Filter Dialog */}
       <Dialog 
         open={filterDialogOpen} 
         onClose={() => setFilterDialogOpen(false)}
         maxWidth="sm"
         fullWidth
       >
         <DialogTitle>
           <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
             Filter & Sort
           </Typography>
         </DialogTitle>
         <DialogContent>
           <Box sx={{ mt: 2 }}>
                           {/* Distance Filter */}
              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Distance
                </Typography>
                <Box sx={{ mt: 2, px: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Up to {distanceValue} km
                  </Typography>
                  <Slider
                    value={distanceValue}
                    onChange={(e, newValue) => setDistanceValue(newValue)}
                    min={1}
                    max={100}
                    marks={[
                      { value: 1, label: '1km' },
                      { value: 25, label: '25km' },
                      { value: 50, label: '50km' },
                      { value: 75, label: '75km' },
                      { value: 100, label: '100km' }
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Box>
              </FormControl>

                             {/* Rating Filter */}
               <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                 <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                   Rating Filter
                 </Typography>
                 <Box sx={{ px: 2 }}>
                   <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                     {minRating === 0 ? 'Any rating' : `${minRating}+ stars`}
                   </Typography>
                   <Slider
                     value={minRating}
                     onChange={(e, newValue) => setMinRating(newValue)}
                     min={0}
                     max={5}
                     step={1}
                     marks={[
                       { value: 0, label: 'Any' },
                       { value: 1, label: '1+' },
                       { value: 2, label: '2+' },
                       { value: 3, label: '3+' },
                       { value: 4, label: '4+' },
                       { value: 5, label: '5' }
                     ]}
                     valueLabelDisplay="auto"
                   />
                 </Box>
               </FormControl>

              {/* Provider Type Filter */}
              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Provider Type
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        defaultChecked={providerTypes.includes('salon')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setProviderTypes(prev => [...prev, 'salon']);
                            setProviderTypeError(null); // Clear error when checking
                          } else {
                            // Prevent unchecking if this would be the last provider type
                            const currentTypes = providerTypes.filter(type => type !== 'salon');
                            if (currentTypes.length === 0) {
                              setProviderTypeError('At least one provider type must be selected');
                              return; // Don't uncheck
                            }
                            setProviderTypes(prev => prev.filter(type => type !== 'salon'));
                            setProviderTypeError(null);
                          }
                        }}
                      />
                    }
                    label="Salon"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        defaultChecked={providerTypes.includes('pros')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setProviderTypes(prev => [...prev, 'pros']);
                            setProviderTypeError(null); // Clear error when checking
                          } else {
                            // Prevent unchecking if this would be the last provider type
                            const currentTypes = providerTypes.filter(type => type !== 'pros');
                            if (currentTypes.length === 0) {
                              setProviderTypeError('At least one provider type must be selected');
                              return; // Don't uncheck
                            }
                            setProviderTypes(prev => prev.filter(type => type !== 'pros'));
                            setProviderTypeError(null);
                          }
                        }}
                      />
                    }
                    label="Pros"
                  />
                </FormGroup>
                {providerTypeError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {providerTypeError}
                  </Alert>
                )}
              </FormControl>

             {/* Categories */}
             <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
               <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                 Categories
               </Typography>
               <FormGroup>
                 {categories.map((category) => (
                   <FormControlLabel
                     key={category.name}
                     control={
                       <Checkbox
                         defaultChecked={selectedCategories.includes(category.name)}
                         onChange={(e) => {
                           if (e.target.checked) {
                             setSelectedCategories(prev => [...prev, category.name]);
                           } else {
                             setSelectedCategories(prev => prev.filter(cat => cat !== category.name));
                           }
                         }}
                       />
                     }
                     label={category.name}
                   />
                 ))}
               </FormGroup>
             </FormControl>

                           {/* Sort By */}
              <FormControl sx={{ mb: 3, width: '100%' }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="rating-high">Rating: High to Low</MenuItem>
                  <MenuItem value="rating-low">Rating: Low to High</MenuItem>
                  <MenuItem value="distance">Distance: Nearest First</MenuItem>
                  <MenuItem value="name">Name: A to Z</MenuItem>
                  <MenuItem value="reviews">Most Reviews</MenuItem>
                </Select>
              </FormControl>
           </Box>
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setFilterDialogOpen(false)}>
             Cancel
           </Button>
           <Button 
             onClick={() => {
               setFilterDialogOpen(false);
             }}
             variant="contained"
             sx={{ backgroundColor: '#1a237e' }}
           >
             Apply Filters
           </Button>
         </DialogActions>
               </Dialog>

        {/* Full-Screen Photo Gallery */}
        <Dialog
          open={galleryOpen}
          onClose={() => setGalleryOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(0,0,0,0.95)',
              color: 'white',
              maxHeight: '90vh'
            }
          }}
        >
          {selectedProvider && (
            <>
              <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: '1px solid rgba(255,255,255,0.2)'
              }}>
                <Typography variant="h6" sx={{ color: 'white' }}>
                  {selectedProvider.name} - Photo Gallery
                </Typography>
                <IconButton
                  onClick={() => setGalleryOpen(false)}
                  sx={{ color: 'white' }}
                >
                  <ClearIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0, textAlign: 'center' }}>
                {selectedProvider.images && selectedProvider.images.length > 0 ? (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={selectedProvider.images[currentImageIndex[selectedProvider.id] || 0]}
                      alt={selectedProvider.name}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '70vh',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.src = '/placeholder-salon.jpg';
                      }}
                    />
                    
                    {/* Gallery Navigation */}
                    {selectedProvider.images.length > 1 && (
                      <>
                        <IconButton
                          onClick={() => {
                            const current = currentImageIndex[selectedProvider.id] || 0;
                            const prev = current === 0 ? selectedProvider.images.length - 1 : current - 1;
                            setCurrentImageIndex(prev => ({
                              ...prev,
                              [selectedProvider.id]: prev
                            }));
                          }}
                          sx={{
                            position: 'absolute',
                            left: 16,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.9)' }
                          }}
                        >
                          ←
                        </IconButton>
                        
                        <IconButton
                          onClick={() => {
                            const current = currentImageIndex[selectedProvider.id] || 0;
                            const next = (current + 1) % selectedProvider.images.length;
                            setCurrentImageIndex(prev => ({
                              ...prev,
                              [selectedProvider.id]: next
                            }));
                          }}
                          sx={{
                            position: 'absolute',
                            right: 16,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.9)' }
                          }}
                        >
                          →
                        </IconButton>
                        
                        {/* Thumbnail Navigation */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          gap: 1, 
                          mt: 2, 
                          pb: 2,
                          overflowX: 'auto'
                        }}>
                          {selectedProvider.images.map((image, index) => (
                            <Box
                              key={index}
                              component="img"
                              src={image}
                              alt={`${selectedProvider.name} ${index + 1}`}
                              onClick={() => {
                                setCurrentImageIndex(prev => ({
                                  ...prev,
                                  [selectedProvider.id]: index
                                }));
                              }}
                              sx={{
                                width: 60,
                                height: 60,
                                objectFit: 'cover',
                                borderRadius: 1,
                                cursor: 'pointer',
                                border: (currentImageIndex[selectedProvider.id] || 0) === index 
                                  ? '3px solid #1a237e' 
                                  : '1px solid rgba(255,255,255,0.3)',
                                opacity: (currentImageIndex[selectedProvider.id] || 0) === index ? 1 : 0.7,
                                '&:hover': { opacity: 1 }
                              }}
                              onError={(e) => {
                                e.target.src = '/placeholder-salon.jpg';
                              }}
                            />
                          ))}
                        </Box>
                      </>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      No photos available
                    </Typography>
                  </Box>
                )}
              </DialogContent>
            </>
          )}
        </Dialog>

       {/* Bottom Navigation */}
       <BottomNavigation />
     </Box>
   );
 };

export default SearchResultsScreen;
